import {
    Absence,
    AbsenceStatus,
    type DateRange,
    type Employee,
    type EmployeeSortProperty,
    EmployeeStatus,
    GLOBAL_CONSTANTS,
    GetAbsencesParams,
    GetRosterNotesParams,
    GetSchoolHolidaysParams,
    type RosterNoteDTO,
    type SchoolHoliday,
    type TimeEntry,
    TimeEntryType,
    type VacationBalance,
    createRosterNote,
    dateAdd,
    dateSub,
    formatDateShort,
    formatWeekDayShort,
    getHolidayForDate,
    getHolidaysInRange,
    getRange,
    getToday,
    getVacationEntitlement,
    matchesFilters,
    monthNames,
    parseDateString,
    timeEntryTypeColor,
    toDateString,
} from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";
import "@pentacode/ui/components";
import anime from "animejs";
import { LitElement, css, html } from "lit";
import { customElement, query, queryAll, state } from "lit/decorators.js";
import { cache } from "lit/directives/cache.js";
import "../../../components/roster-notes/roster-note-list";
import "../../../components/roster-notes/roster-note-popover";
import "../../../elements/absence-dialog";
import type { AbsenceDialog } from "../../../elements/absence-dialog";
import { alert } from "../../../elements/alert-dialog";
import { Checkbox } from "../../../elements/checkbox";
import "../../../elements/month-picker";
import type { MonthPicker } from "../../../elements/month-picker";
import "../../../elements/popover";
import type { Popover } from "../../../elements/popover";
import { app } from "../../../init";
import { singleton } from "../../../lib/singleton";
import { Routing } from "../../../mixins/routing";
import { StateMixin } from "../../../mixins/state";
import { shared } from "../../../styles";
import "../employees-absences-all-row";
import type { EmployeeAbsencesRow, Month } from "../employees-absences-all-row";
import "../employees-filter";

type AbsenceInputEvent = CustomEvent<{ employee: Employee; date: DateString; dragEvent: DragEvent }>;

@customElement("ptc-employees-absences-all")
export class EmployeesAbsencesAll extends Routing(StateMixin(LitElement)) {
    routePattern = /employees\/all\/absences/;

    get routeTitle() {
        return "Abwesenheiten: Alle Mitarbeiter";
    }

    private get _employees() {
        return app.getFilteredEmployees({ from: this._from, to: this._to });
    }

    private get _daysCount() {
        return dateSub(this._from, this._to);
    }

    private get _filteredRosterNotes() {
        const filteredEmployees = this._employees.filter(
            (employee) => employee.status !== EmployeeStatus.Probation && app.employeeMatchesFilters(employee)
        );
        return this._rosterNotes.filter((note) =>
            filteredEmployees.some((employee) => matchesFilters(note.filters, { company: app.company, employee }))
        );
    }

    @state()
    private _loading = false;

    @state()
    private _from: DateString;

    @state()
    private _to: DateString;

    @state()
    private _months: Month[] = [];

    @state()
    private _absences: Absence[] = [];

    @state()
    private _entries: TimeEntry[] = [];

    @state()
    private _vacationBalances: VacationBalance[] = [];

    @state()
    private _vacationBalancesLoading = false;

    @state()
    private _rosterNotes: RosterNoteDTO[] = [];

    @state()
    private _schoolHolidays: SchoolHoliday[] = [];

    @singleton("ptc-absence-dialog")
    private _absenceDialog: AbsenceDialog;

    private _newAbsenceData: {
        employee: Employee;
        start: DateString;
        end: DateString;
        length: number;
        top: number;
        left: number;
        right: number;
    } | null = null;

    private _monthsIntersectionObserver = new IntersectionObserver(
        (entries: IntersectionObserverEntry[]) => this._monthsIntersectionHandler(entries),
        { root: this, rootMargin: "0px 0px 0px -200px" }
    );

    @query(".scrolling")
    private _scroller: HTMLDivElement;

    @query(".absence.dragging", true)
    private _dragElement: HTMLDivElement;

    @query("#dragImage", true)
    private _dragImage: HTMLDivElement;

    @query("ptc-month-picker", true)
    private _monthPicker: MonthPicker;

    @queryAll(".header-row .month")
    private _monthHeaders: HTMLDivElement[];

    @query("#newAbsencePopover")
    private _newAbsencePopover: Popover;

    async handleRoute() {
        if (!this.date) {
            this.go(null, { date: toDateString(new Date()) }, true);
            return;
        }
        this._monthsIntersectionObserver.disconnect();
        await this._setRange(getRange(this.date, "year"));
        await this.updateComplete;
        void this._scrollToDate(this.date);
    }

    private _scrollAnimation: any | null = null;
    private _scrollTarget: DateString | null = null;
    private async _scrollToDate(date: string) {
        const el = this.renderRoot!.querySelector(`#header-${date}`) as HTMLElement;
        const rowHeader = this.renderRoot!.querySelector(".header-row .row-header") as HTMLElement;

        if (this._scrollAnimation) {
            this._scrollAnimation.remove(this._scroller);
        }

        this._monthsIntersectionObserver.disconnect();

        const animation = anime({
            targets: this._scroller,
            scrollLeft: el.offsetLeft - rowHeader.offsetWidth,
            duration: 500,
            easing: "easeInOutCubic",
        });
        this._scrollAnimation = animation;

        await animation.finished;

        if (this._scrollAnimation !== animation) {
            return;
        }
        this._scrollAnimation = null;

        this._scrollTarget = date as DateString;
        this._monthHeaders.forEach((e) => this._monthsIntersectionObserver.observe(e));
    }

    private async _setRange(range: DateRange) {
        if (range.from === this._from && range.to === this._to) {
            return;
        }
        this._from = range.from;
        this._to = range.to;
        const days: string[] = [];
        const months: Month[] = [];
        let month: Month | undefined = undefined;
        let date = this._from;

        while (date < this._to) {
            days.push(date);
            const monthRange = getRange(date, "month");
            if (!month || month.from !== monthRange.from) {
                const d = parseDateString(monthRange.from)!;
                const name = monthNames[d.getMonth()];
                month = {
                    from: monthRange.from,
                    to: monthRange.to,
                    name,
                    year: d.getFullYear(),
                    days: [],
                    isVisible: false,
                };
                months.push(month);
            }
            const holiday = getHolidayForDate(date, {
                holidays: app.venues[0]?.enabledHolidays,
                country: app.company!.country,
            });
            month.days.push({ date, holiday, dayOfWeek: new Date(date).getDay() });
            date = dateAdd(date, { days: 1 });
        }

        this._monthsIntersectionObserver.disconnect();
        this._months = months;

        await this._load();
    }

    private _monthsIntersectionHandler(entries: IntersectionObserverEntry[]) {
        for (const entry of entries) {
            const el = entry.target as HTMLElement;
            const month = this._months.find((m) => m.from === el.dataset.date);
            if (month) {
                month.isVisible = entry.isIntersecting;
            }
        }
        const currMonth = this._scrollTarget ?? this._getScrollMonth();
        this._scrollTarget = null;
        if (currMonth) {
            this._monthPicker.date = currMonth;
            this._monthHeaders.forEach((el) => el.classList.toggle("current-month", el.dataset.date === currMonth));
        }
        this._months = [...this._months];
    }

    private _getScrollMonth(): DateString | undefined {
        const rowHeader = this.renderRoot.querySelector(".header-row .row-header") as HTMLElement;
        if (!this._scroller || !rowHeader) {
            return this._months.find((m) => m.isVisible)?.from;
        }
        const viewLeft = this._scroller.scrollLeft + rowHeader.offsetWidth;
        const viewRight = this._scroller.scrollLeft + this._scroller.clientWidth;
        let current: DateString | undefined;
        let maxVisible = 0;
        for (const header of this._monthHeaders) {
            const visibleStart = Math.max(header.offsetLeft, viewLeft);
            const visibleEnd = Math.min(header.offsetLeft + header.offsetWidth, viewRight);
            const visibleWidth = Math.max(0, visibleEnd - visibleStart);
            if (visibleWidth > maxVisible) {
                maxVisible = visibleWidth;
                current = header.dataset.date as DateString | undefined;
            }
        }
        return current;
    }

    private _rowsInteractionObserver?: IntersectionObserver;

    private _rowsIntersectionHandler(entries: IntersectionObserverEntry[]) {
        entries.forEach((e) => ((e.target as EmployeeAbsencesRow).isVisible = e.isIntersecting));
    }

    private _observeRows() {
        if (!this._rowsInteractionObserver) {
            const scroller = this.renderRoot.querySelector(".scrolling");
            if (!scroller) {
                return;
            }
            this._rowsInteractionObserver = new IntersectionObserver(
                (entries: IntersectionObserverEntry[]) => this._rowsIntersectionHandler(entries),
                { root: scroller, rootMargin: "100%" }
            );
        }
        const elements = this.renderRoot.querySelectorAll("ptc-employee-absences-row");
        for (const el of elements) {
            this._rowsInteractionObserver.observe(el);
        }
    }

    updated() {
        this._observeRows();
    }

    private async _loadRosterNotes(showSpinner = false) {
        if (showSpinner) {
            this._loading = true;
        }

        try {
            this._rosterNotes = await app.api.getRosterNotes(
                new GetRosterNotesParams({
                    from: this._from,
                    to: this._to,
                })
            );
        } catch (e) {
            console.error(e);
        }

        if (showSpinner) {
            this._loading = false;
        }
    }

    private async _loadBalancesInBackground() {
        this._vacationBalancesLoading = true;
        try {
            const balances = await app.api.getVacationBalances({
                ...getRange(new Date(this._from), "year"),
                filters: [],
                resolution: "year",
            });
            this._vacationBalances = balances;
        } catch (e) {
            void alert(e.message, { type: "warning" });
        } finally {
            this._vacationBalancesLoading = false;
        }
    }

    private async _load() {
        if (this._loading) return;

        this._loading = true;

        void this._loadBalancesInBackground();

        try {
            const [absences, timeEntries, schoolHolidays] = await Promise.all([
                app.api.getAbsences(
                    new GetAbsencesParams({
                        from: this._from,
                        to: this._to,
                        status: [AbsenceStatus.Approved, AbsenceStatus.Requested],
                    })
                ),
                app.getTimeEntries({
                    from: this._from,
                    to: this._to,
                    type: [TimeEntryType.Free, TimeEntryType.CompDay],
                }),
                app.api.getSchoolHolidays(
                    new GetSchoolHolidaysParams({
                        from: this._from,
                        to: this._to,
                        country: app.company.country,
                        states: app.venues.map((venue) => venue.state),
                    })
                ),
                this._loadRosterNotes(),
            ]);

            this._absences = absences;
            this._entries = timeEntries;
            this._schoolHolidays = schoolHolidays;
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }
        this._loading = false;
    }

    private async _dayClicked({ detail: { dragEvent, employee, date } }: AbsenceInputEvent) {
        const el = dragEvent.target as HTMLElement;
        this._newAbsenceData = {
            employee,
            start: date,
            end: dateAdd(date, { days: 1 }),
            length: 1,
            top: el.offsetTop,
            left: el.offsetLeft,
            right: el.offsetLeft + el.offsetWidth,
        };
        this._updateDragElement();
        return this._openNewAbsencePopover(true);
    }

    private async _openNewAbsencePopover(ignoreFirstClick = false) {
        if (this._newAbsenceData) {
            await this._newAbsencePopover.showAt(this._dragElement, ignoreFirstClick);
        }
        this._dragElement.classList.add("invisible");
        this._newAbsenceData = null;
    }

    private async _newAbsence(type: TimeEntryType) {
        if (!this._newAbsenceData) {
            return;
        }

        const created = await this._absenceDialog.show(
            new Absence({
                employeeId: this._newAbsenceData.employee.id,
                start: this._newAbsenceData.start,
                end: this._newAbsenceData.end,
                type,
            })
        );
        if (created) {
            return this._load();
        }
    }

    private async _editAbsence(absence: Absence) {
        const edited = await this._absenceDialog.show(absence);
        if (edited) {
            return this._load();
        }
    }

    private _updateDragElement() {
        const data = this._newAbsenceData;
        const el = this._dragElement;
        if (!data || !el) {
            return;
        }
        el.classList.remove("invisible");
        el.style.top = `${data.top}px`;
        el.style.left = `${data.left}px`;
        el.style.width = `${data.right - data.left}px`;
        (el.querySelector(".range") as HTMLDivElement).innerText = `${formatDateShort(data.start)} - ${formatDateShort(
            dateAdd(data.end, { days: -1 })
        )}`;
        (el.querySelector(".length") as HTMLDivElement).innerText = `${data.length} T`;
    }

    private async _dragStart({ detail: { dragEvent, employee, date } }: AbsenceInputEvent) {
        const el = dragEvent.target as HTMLElement;
        dragEvent.dataTransfer!.setDragImage(this._dragImage, 0, 0);
        dragEvent.dataTransfer!.effectAllowed = "copy";
        dragEvent.dataTransfer!.dropEffect = "copy";
        dragEvent.dataTransfer!.setData("text/plain", "blah");
        this._newAbsenceData = {
            employee,
            start: date,
            end: dateAdd(date, { days: 1 }),
            length: 1,
            top: el.offsetTop,
            left: el.offsetLeft,
            right: el.offsetLeft + el.offsetWidth,
        };

        this._updateDragElement();
    }

    private async _dragOver({ detail: { dragEvent } }: AbsenceInputEvent) {
        dragEvent.preventDefault();
        dragEvent.dataTransfer!.dropEffect = "copy";
    }

    private async _dragEnter({ detail: { dragEvent, date } }: AbsenceInputEvent) {
        dragEvent.preventDefault();
        if (this._newAbsenceData) {
            const el = dragEvent.target as HTMLElement;
            this._newAbsenceData.end =
                date > this._newAbsenceData.start ? date : dateAdd(this._newAbsenceData.start, { days: 1 });
            this._newAbsenceData.length = dateSub(this._newAbsenceData.start, this._newAbsenceData.end);
            this._newAbsenceData.right = el.offsetLeft + el.offsetWidth;
            this._updateDragElement();
        }
    }

    private async _dragEnd() {
        return this._openNewAbsencePopover();
    }

    static styles = [
        shared,
        Checkbox.styles,
        css`
            :host {
                --cell-size: 2em;
            }

            .scroller-inner {
                padding-right: calc(100% - 8 * var(--cell-size));
            }

            .row {
                border-bottom: solid 1px var(--shade-2);
                box-sizing: border-box;
                height: 2em;
                contain: size style;
            }

            .header-row {
                background: var(--color-bg);
                position: sticky;
                top: 0;
                z-index: 3;
                border-bottom: solid 1px var(--shade-2);
            }

            .roster-notes-row {
                top: 5.2em;
            }

            .month {
                box-shadow: inset var(--shade-2) -1px 0 0 0px;
                box-sizing: border-box;
                scroll-snap-align: start;
            }

            .month-name {
                padding: 0.9em 1em;
                font-weight: 600;
                transition: all 0.5s;
            }

            .month.current-month .month-name {
                transform: translateX(-50%);
                opacity: 0;
            }

            .row-header {
                background: rgba(255, 255, 255, 0.9);
                border-right: solid 1px var(--shade-2);
                width: calc(8 * var(--cell-size));
                position: sticky;
                left: 0;
                box-sizing: border-box;
                z-index: 1;
            }

            .row-header ptc-ui-avatar {
                font-size: 0.5em;
                margin-left: 0.5em;
            }

            .row-header .vacation {
                margin-right: 0.2em;
                width: 6em;
            }

            .day {
                height: var(--cell-size);
                width: var(--cell-size);
                box-sizing: border-box;
                color: var(--color-highlight);
                background: var(--color-highlight-bg);
                padding: 0.25em;
            }

            .day:not(:last-child) {
                border-right: dashed 1px var(--shade-2);
            }

            .absence {
                position: absolute;
                height: calc(var(--cell-size) - 1px);
                box-sizing: border-box;
            }

            .absence-inner {
                background: var(--color-highlight);
                color: var(--color-bg);
                border-radius: 0.5em;
                position: absolute;
                top: 0.1em;
                left: 0.1em;
                right: 0.1em;
                bottom: 0.1em;
                padding: 0 0.2em;
                overflow: hidden;
                box-sizing: border-box;
                border: solid 1px transparent;
                line-height: 2em;
            }

            .absence.requested .absence-inner {
                border-color: var(--color-highlight);
                background: var(--color-bg);
                color: var(--color-highlight);
            }

            .dragging {
                opacity: 0.7;
                pointer-events: none;
            }

            .today {
                border-radius: 100%;
                background: var(--blue);
                color: var(--color-bg);
            }

            .roster-notes {
                position: sticky;
                top: 5.2em;
                background: var(--color-bg);
                z-index: 2;
            }

            ptc-ui-progress.vacation {
                --progress-color: var(--blue);
                --progress-color-above: var(--blue);
            }

            @keyframes pulse {
                0% {
                    opacity: 0.3;
                }
                50% {
                    opacity: 1;
                }
                100% {
                    opacity: 0.3;
                }
            }

            .loading-pulse {
                animation: pulse 2.5s ease-in-out infinite;
            }
        `,
    ];

    private _renderHeader() {
        const today = toDateString(new Date());
        return html`
            <div class="horizontal layout header-row">
                <div class="row-header vertical layout"></div>
                ${this._months.map(
                    ({ name, days, year, from }) => html`
                        <div class="month vertical start-aligning layout" data-date="${from}">
                            <div class="month-name stretch">${name} ${year}</div>
                            <div class="horizontal layout">
                                ${days.map(({ date, dayOfWeek, holiday }) => {
                                    const d = parseDateString(date)!;
                                    return html`
                                        <div
                                            class="text-centering day ${holiday
                                                ? "violet"
                                                : dayOfWeek === 0
                                                  ? "orange"
                                                  : ""}"
                                            title=${holiday
                                                ? holiday.name
                                                : `${formatWeekDayShort(date)}, ${formatDateShort(date)}`}
                                            id="header-${date}"
                                        >
                                            <div class="${date === today ? "today" : ""}">${d.getDate()}</div>
                                        </div>
                                    `;
                                })}
                            </div>
                        </div>
                    `
                )}
            </div>
        `;
    }

    private _renderRosterNotes() {
        return html`
            <div class="header-row roster-notes-row horizontal layout">
                <div class="row-header semibold">
                    <div class="center-aligning horizontal layout">
                        <div class="stretch half-padded">
                            <ptc-ui-icon name="icon.object.date"></ptc-ui-icon>
                            Kalendereinträge
                        </div>
                        <button class="smaller skinny transparent right-margined">
                            <ptc-ui-icon name="icon.action.add"></ptc-ui-icon>
                        </button>
                        <ptc-roster-note-popover
                            .rosterNote=${createRosterNote({ companyId: app.company.id })}
                            @done=${() => this._loadRosterNotes(true)}
                        ></ptc-roster-note-popover>
                    </div>
                </div>
                <ptc-roster-notes
                    style="width: calc(${this._daysCount} * var(--cell-size))"
                    .rosterNotes=${this._filteredRosterNotes}
                    .schoolHolidays=${this._schoolHolidays}
                    .holidays=${getHolidaysInRange(this._from, this._to, {
                        holidays: app.venues.flatMap((venue) => venue.enabledHolidays),
                        country: app.company.country,
                    })}
                    .dateRange=${{ from: this._from, to: this._to }}
                    @roster-notes-updated=${() => this._loadRosterNotes(true)}
                ></ptc-roster-notes>
            </div>
        `;
    }

    render() {
        if (!this._from || !this._to || !this._months) {
            return html`
                <div class="fullbleed centering layout scrim">
                    <ptc-ui-spinner active></ptc-ui-spinner>
                </div>
            `;
        }

        const employees = this._employees;
        const daysCount = this._daysCount;

        return html`
            <div class="fullbleed vertical layout">
                <div class="padded horizontal spacing center-aligning layout border-bottom">
                    <ptc-month-picker
                        .max=${dateAdd(getToday(), { months: GLOBAL_CONSTANTS.VACATION_FUTURE_LIMIT_MONTHS })}
                        @change=${({ detail: { date } }: CustomEvent<{ date: string }>) => this.go(null, { date })}
                    ></ptc-month-picker>
                    <div class="stretch"></div>
                </div>
                <ptc-employees-filter
                    class="border-bottom"
                    .sortableProps=${["firstName", "lastName", "staffNumber"] as EmployeeSortProperty[]}
                ></ptc-employees-filter>
                <div class="scrolling stretch">
                    <div class="scroller-inner" style="width: calc(${daysCount + 8} * var(--cell-size))">
                        ${this._renderHeader()} ${this._renderRosterNotes()}
                        <div class="relative">
                            ${employees.map((employee) => {
                                const filtered =
                                    employee.status === EmployeeStatus.Probation ||
                                    !app.employeeMatchesFilters(employee);
                                return cache(
                                    filtered
                                        ? null
                                        : html`
                                              <ptc-employee-absences-row
                                                  class="row horizontal layout"
                                                  .employee=${employee}
                                                  .absences=${this._absences}
                                                  .vacationEntitlement=${getVacationEntitlement(
                                                      this._vacationBalances.find((b) => b.employeeId === employee.id)
                                                  )}
                                                  .vacationBalancesLoading=${this._vacationBalancesLoading}
                                                  .entries=${this._entries}
                                                  .months=${this._months}
                                                  .from=${this._from}
                                                  .to=${this._to}
                                                  @day-clicked=${this._dayClicked}
                                                  @absence-clicked=${(e: CustomEvent<{ absence: Absence }>) =>
                                                      this._editAbsence(e.detail.absence)}
                                                  @drag-start=${this._dragStart}
                                                  @drag-over=${this._dragOver}
                                                  @drag-enter=${this._dragEnter}
                                                  @drag-end=${this._dragEnd}
                                                  @drop=${(e: Event) => e.preventDefault()}
                                              ></ptc-employee-absences-row>
                                          `
                                );
                            })}

                            <div class="grey absence dragging invisible">
                                <div class="absence-inner spacing center-aligning horizontal layout click">
                                    <ptc-ui-icon name="icon.action.help" class="smaller"></ptc-ui-icon>
                                    <div class="stretch ellipsis range"></div>
                                    <div class="subtle semibold length"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ptc-popover id="newAbsencePopover" class="larger tooltip" hide-on-click .anchor=${null}>
                <div class="vertical spacing layout">
                    ${[
                        TimeEntryType.Vacation,
                        TimeEntryType.Sick,
                        TimeEntryType.ChildSick,
                        TimeEntryType.SickInKUG,
                    ].map(
                        (type) => html`
                            <button
                                class="slim ghost"
                                @click=${() => this._newAbsence(type)}
                                style="--color-highlight: ${timeEntryTypeColor(type)}"
                            >
                                <ptc-ui-icon name="${app.localized.timeEntryTypeIcon(type)}"></ptc-ui-icon>
                                ${app.localized.timeEntryTypeLabel({ type })}
                            </button>
                        `
                    )}
                </div>
            </ptc-popover>

            <div class="fullbleed centering layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner .active=${this._loading}></ptc-ui-spinner>
            </div>
            <img
                id="dragImage"
                src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
            />
        `;
    }
}
